/**
 * Toast vs Banner - which one to use:
 *
 *   Toast (this provider)  -> transient + GLOBAL feedback that isn't tied to a
 *     spot on the page: sync queued/started acks, bulk-action results,
 *     copy-to-clipboard, one-off API errors. Auto-hides (error persists).
 *
 *   Banner (do NOT use this) -> persistent + CONTEXTUAL state anchored to the
 *     data it describes: data-freshness/connection alerts (AlertStack) and
 *     selection toolbars (BulkActionBanner). These stay until the underlying
 *     condition clears - a toast would lose that context.
 *
 * Toasts use one compact dark charcoal snackbar for every severity (Gmail/
 * Sheets style), centred at the bottom of the viewport. The ONLY severity cue
 * is the icon colour (success=green, error=marketing red, warning=amber,
 * info/sync=cyan) - no coloured accent or background. The action is a text-
 * only link in muted cyan; the close icon is lower-emphasis. Inline banners
 * stay on light soft-tinted surfaces - they are persistent page furniture,
 * not floating pop-ups.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { Box, Button, IconButton, Typography } from '@mui/material'
import {
  CheckCircleOutline as SuccessIcon,
  ErrorOutline as ErrorIcon,
  WarningAmberOutlined as WarningIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { Link } from 'react-router-dom'
import { useAppInfo } from '../../hooks/useVersion'
import { poolwerxColors } from '../../theme/colors'
import {
  BOTTOM_CHROME_RESERVE_PX,
  BOTTOM_CHROME_Z_INDEX,
} from '../feedback/bottomChrome'
import {
  ErrorSeverity,
  ErrorToastConfig,
  extractErrorMessage,
  handleApiErrorWithToast,
} from './errorToastHelpers'

// Re-export so existing importers of these symbols from ErrorToast keep working.
export type { ErrorSeverity, ErrorToastConfig }
export { extractErrorMessage, handleApiErrorWithToast }

/**
 * Toast context value. `notify` is the unified entry point; the `showX`
 * wrappers are kept for ergonomics and RULES naming (and because existing
 * consumers - e.g. BulkActionBanner - depend on them).
 */
interface ErrorToastContextValue {
  notify: (config: ErrorToastConfig) => void
  showError: (config: ErrorToastConfig | string) => void
  showSuccess: (message: string, title?: string) => void
  showWarning: (message: string, title?: string) => void
  showInfo: (message: string, title?: string) => void
  hideError: () => void
}

const ErrorToastContext = createContext<ErrorToastContextValue | null>(null)

/**
 * No-op fallback when no provider is in the tree. The provider is mounted at
 * the app root (`main.tsx`), so in production this is never hit. The only
 * realistic caller without a provider is a test harness rendering a shared
 * component in isolation; a toast is non-critical, so degrade to no-ops.
 */
const NOOP_TOAST: ErrorToastContextValue = {
  notify: () => {},
  showError: () => {},
  showSuccess: () => {},
  showWarning: () => {},
  showInfo: () => {},
  hideError: () => {},
}

export const useErrorToast = () => {
  const context = useContext(ErrorToastContext)
  return context ?? NOOP_TOAST
}

/** Alias: durable-notifications work consumes the same provider/hook. */
export const useNotify = useErrorToast

// --- Severity tokens -------------------------------------------------------
// On the uniform dark card, severity reads ONLY through the icon colour, using
// the softer toast palette (brand status colours are too saturated on dark).
interface SeverityToken {
  Icon: React.ComponentType<{ sx?: object }>
  accent: string
}

const SEVERITY: Record<ErrorSeverity, SeverityToken> = {
  success: { Icon: SuccessIcon, accent: poolwerxColors.toastSuccess },
  error: { Icon: ErrorIcon, accent: poolwerxColors.toastError },
  warning: { Icon: WarningIcon, accent: poolwerxColors.toastWarning },
  info: { Icon: InfoIcon, accent: poolwerxColors.toastInfo },
}

// Auto-hide per severity. error is null => persists until dismissed.
const AUTO_HIDE_MS: Record<ErrorSeverity, number | null> = {
  success: 6000,
  info: 6000,
  warning: 8000,
  error: null,
}

// Newest on top; older toasts beyond this drop off the bottom of the stack.
const MAX_VISIBLE = 3

interface ActiveToast extends ErrorToastConfig {
  id: number
  severity: ErrorSeverity
  /** Resolved auto-hide; null means it persists. */
  autoHideMs: number | null
}

const resolveAutoHide = (config: ErrorToastConfig, severity: ErrorSeverity): number | null => {
  if (config.persistent) return null
  if (config.duration != null) return config.duration
  return AUTO_HIDE_MS[severity]
}

interface ToastItemProps {
  toast: ActiveToast
  onDismiss: (id: number) => void
}

/**
 * Single toast row. Owns its own auto-hide timer so each toast in the stack
 * expires independently; hover/focus pauses it (re-arming gives a fresh full
 * window, which is the friendly behaviour when a user pauses to read).
 */
const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  const [paused, setPaused] = useState(false)
  const { Icon, accent } = SEVERITY[toast.severity]
  const isError = toast.severity === 'error'

  useEffect(() => {
    if (toast.autoHideMs == null || paused) return
    const timer = setTimeout(() => onDismiss(toast.id), toast.autoHideMs)
    return () => clearTimeout(timer)
  }, [toast.autoHideMs, toast.id, paused, onDismiss])

  const hasInlineAction = Boolean(toast.actionUrl || toast.action)

  return (
    <Box
      // error is assertive (interrupts a screen reader); the rest are polite.
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      sx={{
        pointerEvents: 'auto',
        display: 'flex',
        // Compact single-row snackbar (Gmail/Sheets style): icon · message ·
        // flexible spacer · action · close, all vertically centred.
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        minHeight: 52,
        py: 1,
        px: 2,
        borderRadius: '8px',
        // One uniform charcoal card for every severity - the only severity cue
        // is the icon colour (no coloured accent or background).
        backgroundColor: poolwerxColors.toastSurface,
        // Subtle lift, not a floating-modal shadow.
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      }}
    >
      <Icon sx={{ color: accent, fontSize: 18, flexShrink: 0 }} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {toast.title && (
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: poolwerxColors.toastText }}>
            {toast.title}
          </Typography>
        )}
        <Typography
          sx={{
            fontSize: '0.875rem',
            // Light grey/white - high-emphasis text on the dark card.
            color: poolwerxColors.toastText,
            // Wrap long copy, but clamp to 2 lines so a verbose message can't
            // grow the toast unbounded; break long unbroken tokens (URLs).
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
        >
          {toast.message}
        </Typography>
        {toast.subMessage && (
          <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', mt: 0.25 }}>
            {toast.subMessage}
          </Typography>
        )}
      </Box>

      {hasInlineAction &&
        (toast.actionUrl ? (
          // RULES: navigation is a <Link>, never onClick/navigate().
          <Button
            component={Link}
            to={toast.actionUrl}
            size="small"
            disableRipple
            onClick={() => onDismiss(toast.id)}
            sx={{
              // Text-only link, not a button: no fill/ripple, muted cyan.
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: poolwerxColors.toastAction,
              flexShrink: 0,
              minWidth: 0,
              p: 0,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
                color: poolwerxColors.toastActionHover,
                textDecoration: 'underline',
              },
            }}
          >
            {toast.actionLabel ?? 'View'}
          </Button>
        ) : (
          toast.action
        ))}

      <IconButton
        size="small"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(toast.id)}
        sx={{
          flexShrink: 0,
          // Muted, lower emphasis than the action link.
          color: poolwerxColors.toastClose,
          opacity: 0.7,
          transition: 'opacity 120ms ease',
          '&:hover': { opacity: 1 },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

interface ToastProviderProps {
  children: React.ReactNode
  defaultDuration?: number
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const { environment } = useAppInfo()
  const isDev = environment === 'development'
  const [toasts, setToasts] = useState<ActiveToast[]>([])
  const idRef = useRef(0)

  const dismiss = useCallback((id: number) => {
    setToasts(prev => {
      const target = prev.find(t => t.id === id)
      target?.onClose?.()
      return prev.filter(t => t.id !== id)
    })
  }, [])

  const present = useCallback((next: ErrorToastConfig) => {
    const severity = next.severity ?? 'info'
    const id = (idRef.current += 1)
    const toast: ActiveToast = {
      ...next,
      id,
      severity,
      autoHideMs: resolveAutoHide(next, severity),
    }
    // Append (newest last); the container renders bottom-up so it sits on
    // top. Cap the stack, dropping the oldest still-visible toast.
    setToasts(prev => [...prev, toast].slice(-MAX_VISIBLE))
  }, [])

  const notify = useCallback((cfg: ErrorToastConfig) => present(cfg), [present])

  const showError = useCallback(
    (errorConfig: ErrorToastConfig | string) =>
      present(
        typeof errorConfig === 'string'
          ? { message: errorConfig, severity: 'error' }
          : { severity: 'error', ...errorConfig },
      ),
    [present],
  )

  const showSuccess = useCallback(
    (message: string, title?: string) => present({ message, title, severity: 'success' }),
    [present],
  )

  const showWarning = useCallback(
    (message: string, title?: string) => present({ message, title, severity: 'warning' }),
    [present],
  )

  const showInfo = useCallback(
    (message: string, title?: string) => present({ message, title, severity: 'info' }),
    [present],
  )

  // Back-compat: legacy callers used hideError() to close "the" toast.
  const hideError = useCallback(() => setToasts([]), [])

  // Anchored bottom-centre of the viewport (Google Calendar-style snackbar):
  // left:50% + translateX(-50%) centres regardless of the admin sidebar.
  // bottom = 24px, raised to clear the sticky DevEnvironmentBanner in dev.
  const bottomOffset = (isDev ? BOTTOM_CHROME_RESERVE_PX : 0) + 24

  return (
    <ErrorToastContext.Provider
      value={{ notify, showError, showSuccess, showWarning, showInfo, hideError }}
    >
      {children}
      <Box
        aria-label="Notifications"
        sx={{
          position: 'fixed',
          bottom: bottomOffset,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: BOTTOM_CHROME_Z_INDEX,
          // Container is click-through; each toast re-enables pointer events.
          pointerEvents: 'none',
          display: 'flex',
          // Newest (last in array) renders on top of the stack (grows upward);
          // centre narrower toasts within the 400px column.
          flexDirection: 'column-reverse',
          alignItems: 'center',
          gap: 1,
          width: 550,
          maxWidth: 'calc(100vw - 48px)',
        }}
      >
        {toasts.map(toast => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </Box>
    </ErrorToastContext.Provider>
  )
}

export default ToastProvider
